import { useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { fileUrl } from "@/lib/api";
import api from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import RichTextEditor from "@/components/RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArcButton } from "@/components/ArcButton";
import { Pencil, Check, X, ImagePlus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

/**
 * Editable text. modes: "text" (single line), "multiline", "rich" (HTML).
 * Non-admins see plain content; admins get click/hover-to-edit inline.
 */
export function Editable({ id, as: Tag = "span", mode = "text", fallback = "", className = "", style }) {
  const { isAdmin } = useAuth();
  const { get, save } = useContent();
  const raw = get(id, fallback);
  const isObj = raw && typeof raw === "object" && !Array.isArray(raw);
  const value = isObj ? (raw.text ?? "") : raw;
  const size = isObj ? (raw.size || "md") : "md";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [draftSize, setDraftSize] = useState("md");
  const [dlgOpen, setDlgOpen] = useState(false);

  const SIZE_EM = { sm: "0.85em", md: null, lg: "1.3em", xl: "1.7em" };
  const sizedStyle = SIZE_EM[size] ? { ...style, fontSize: SIZE_EM[size] } : style;

  if (!isAdmin) {
    if (mode === "rich") return <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />;
    return <Tag className={className} style={sizedStyle}>{value}</Tag>;
  }

  // ---- Rich text (dialog) ----
  if (mode === "rich") {
    return (
      <>
        <div className="group relative">
          <Tag className={className} style={style} dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }} />
          <button
            data-testid={`edit-${id}`}
            onClick={() => { setDraft(value); setDlgOpen(true); }}
            className="absolute -right-2 -top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full border bg-white text-black opacity-0 shadow transition-opacity group-hover:opacity-100 dark:bg-zinc-800 dark:text-white"
            title="Edit"
          ><Pencil size={13} /></button>
        </div>
        <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
          <DialogContent className="max-w-2xl" style={{ backgroundColor: "var(--arc-card)" }}>
            <DialogHeader><DialogTitle>Edit content</DialogTitle></DialogHeader>
            <RichTextEditor value={draft} onChange={setDraft} testId={`rich-${id}`} />
            <div className="flex gap-2">
              <ArcButton variant="ghoulax" onClick={() => { save(id, draft, "rich"); setDlgOpen(false); }} data-testid={`save-${id}`}>Save</ArcButton>
              <ArcButton variant="outline" onClick={() => setDlgOpen(false)}>Cancel</ArcButton>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ---- Inline text / multiline ----
  if (editing) {
    const commit = () => { save(id, { text: draft, size: draftSize }, mode); setEditing(false); };
    const cancel = () => setEditing(false);
    const shared = {
      autoFocus: true,
      value: draft,
      onChange: (e) => setDraft(e.target.value),
      "data-testid": `input-${id}`,
      className: "w-full rounded border px-1.5 py-0.5 outline-none focus:ring-2",
      style: { fontFamily: "inherit", fontSize: "inherit", fontWeight: "inherit", lineHeight: "inherit", color: "var(--arc-text)", backgroundColor: "var(--arc-bg)" },
    };
    const sizeOpts = [{ k: "sm", l: "S" }, { k: "md", l: "M" }, { k: "lg", l: "L" }, { k: "xl", l: "XL" }];
    return (
      <span className="relative inline-flex w-full max-w-full flex-col items-start gap-1">
        {mode === "multiline"
          ? <textarea rows={4} onKeyDown={(e) => { if (e.key === "Escape") cancel(); }} {...shared} />
          : <input onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") cancel(); }} {...shared} />}
        <span className="flex flex-wrap items-center gap-1">
          <span className="mr-1 flex items-center overflow-hidden rounded border" data-testid={`size-${id}`}>
            {sizeOpts.map((o) => (
              <button key={o.k} onClick={() => setDraftSize(o.k)} data-testid={`size-${id}-${o.k}`}
                className={`h-6 w-7 text-xs font-bold ${draftSize === o.k ? "" : ""}`}
                style={draftSize === o.k ? { backgroundColor: "var(--ghoulax)", color: "var(--ghoulax-fg)" } : {}}>{o.l}</button>
            ))}
          </span>
          <button onClick={commit} data-testid={`commit-${id}`} className="flex h-6 items-center gap-1 rounded bg-emerald-600 px-2 text-xs font-semibold text-white"><Check size={12} /> Save</button>
          <button onClick={cancel} className="flex h-6 items-center gap-1 rounded border px-2 text-xs"><X size={12} /> Cancel</button>
        </span>
      </span>
    );
  }

  return (
    <Tag
      className={`arc-editable ${className}`}
      style={sizedStyle}
      onClick={() => { setDraft(value); setDraftSize(size); setEditing(true); }}
      data-testid={`editable-${id}`}
      title="Click to edit"
    >
      {value}
      <Pencil size={12} className="arc-editable-pencil" />
    </Tag>
  );
}

/** Editable single image slot. */
export function EditableImage({ id, className = "", fallback = "", alt = "", label = "image" }) {
  const { isAdmin } = useAuth();
  const { get, save } = useContent();
  const value = get(id, fallback);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const upload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await save(id, data.url, "image");
    } catch (err) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  if (!isAdmin) {
    if (!value) return null;
    return <img src={fileUrl(value)} alt={alt} className={className} />;
  }

  return (
    <div className={`group relative ${value ? "" : "flex min-h-[120px] items-center justify-center rounded-md border-2 border-dashed"}`} style={value ? {} : { borderColor: "var(--arc-border)" }}>
      {value
        ? <img src={fileUrl(value)} alt={alt} className={className} />
        : <span className="text-sm" style={{ color: "var(--arc-text-2)" }}>No {label} yet</span>}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`image-input-${id}`} />
      <button
        onClick={() => inputRef.current.click()}
        disabled={busy}
        data-testid={`image-btn-${id}`}
        className="absolute right-2 top-2 flex items-center gap-1 rounded-md border bg-white px-2.5 py-1.5 text-xs font-semibold text-black shadow opacity-0 transition-opacity group-hover:opacity-100 dark:bg-zinc-800 dark:text-white"
      >
        {value ? <Upload size={13} /> : <ImagePlus size={13} />} {busy ? "Uploading…" : value ? "Change" : `Add ${label}`}
      </button>
    </div>
  );
}

/** A zone where admins can drop any number of image blocks anywhere on a page. */
export function ImageBlockZone({ id, className = "" }) {
  const { isAdmin } = useAuth();
  const { get, save } = useContent();
  const blocks = get(id, []) || [];
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const add = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = [...blocks, { id: `${Date.now()}`, url: data.url, caption: "" }];
      await save(id, next, "blocks");
    } catch (err) { toast.error("Upload failed"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = async (bid) => { await save(id, blocks.filter((b) => b.id !== bid), "blocks"); };
  const setCaption = async (bid, caption) => {
    const current = blocks.find((b) => b.id === bid);
    if (!current || current.caption === caption) return;
    await save(id, blocks.map((b) => b.id === bid ? { ...b, caption } : b), "blocks", { silent: true });
  };

  if (!isAdmin && blocks.length === 0) return null;

  return (
    <div className={`space-y-4 ${className}`} data-testid={`imageblocks-${id}`}>
      {blocks.map((b) => (
        <figure key={b.id} className="group relative overflow-hidden rounded-md border" style={{ backgroundColor: "var(--arc-card)" }}>
          <img src={fileUrl(b.url)} alt={b.caption || ""} className="w-full object-cover" />
          {isAdmin ? (
            <>
              <input
                defaultValue={b.caption}
                onBlur={(e) => setCaption(b.id, e.target.value)}
                placeholder="Add a caption (optional)"
                className="w-full border-t bg-transparent px-3 py-2 text-sm outline-none"
                style={{ color: "var(--arc-text-2)" }}
                data-testid={`caption-${b.id}`}
              />
              <button onClick={() => remove(b.id)} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded bg-red-600 text-white opacity-0 transition-opacity group-hover:opacity-100" data-testid={`remove-block-${b.id}`}><Trash2 size={14} /></button>
            </>
          ) : (
            b.caption && <figcaption className="border-t px-3 py-2 text-sm" style={{ color: "var(--arc-text-2)" }}>{b.caption}</figcaption>
          )}
        </figure>
      ))}
      {isAdmin && (
        <>
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={add} data-testid={`addblock-input-${id}`} />
          <button
            onClick={() => inputRef.current.click()}
            disabled={busy}
            data-testid={`addblock-${id}`}
            className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-4 text-sm font-semibold transition-colors hover:bg-black/5 dark:hover:bg-white/5"
            style={{ borderColor: "var(--arc-border)", color: "var(--arc-text-2)" }}
          >
            <ImagePlus size={16} /> {busy ? "Uploading…" : "Add an image here"}
          </button>
        </>
      )}
    </div>
  );
}
